// src/components/admin/Payments.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './Payments.css';

const PAYMENT_LABELS = { link_sent: 'Link Sent', paid: 'Paid', failed: 'Failed' };
const SUB_LABELS = { payment_due: 'Payment Due', active: 'Active', grace_period: 'Grace Period', cancelled: 'Cancelled' };

const EMPTY_NEW_SUB = {
  source_id: '', // holds "lead:3" or "customer:7" — split apart on submit
  plan_name: 'Starter Store',
  monthly_amount: '12000',
  payment_link: ''
};

const Payments = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ links_sent: 0, paid: 0, failed_or_grace: 0, disabled_stores: 0, monthly_revenue: 0 });
  const [eligibleLeads, setEligibleLeads] = useState([]);
  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showLeads, setShowLeads] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [newSub, setNewSub] = useState(EMPTY_NEW_SUB);
  const [submitting, setSubmitting] = useState(false);

  // Edit drawer state
  const [editDrawer, setEditDrawer] = useState(null); // holds the subscription being edited, or null
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://creativepulse.pk/main-api/get-payments.php');
      const data = await res.json();
      if (data.success) {
        setSubscriptions(data.subscriptions);
        setStats(data.stats);
        setEligibleLeads(data.eligible_leads);
        setEligibleCustomers(data.eligible_customers || []);
      }
    } catch (e) {
      console.error('Error fetching payments:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    setActionLoadingId(id);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/update-subscription.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action })
      });
      const data = await res.json();
      if (data.success) {
        fetchPayments();
      } else {
        alert(data.message || 'Action failed');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopy = (link) => {
    if (!link) {
      alert('No payment link set for this client.');
      return;
    }
    navigator.clipboard.writeText(link);
  };

  // TODO: no email/WhatsApp reminder system wired to this specific action
  // yet — same gap as ticket notifications. Left honest rather than fake.
  const handleRemind = () => {
    alert('Reminder sending is not connected to a notification system yet.');
  };

  // Cascading dropdown options — union of whichever checkbox(es) are checked.
  // Each option is tagged (type + real id) so submit knows which field to fill.
  const dropdownOptions = useMemo(() => {
    const opts = [];
    if (showLeads) {
      eligibleLeads.forEach(l => opts.push({
        value: `lead:${l.id}`,
        label: `${l.lead_name}${l.business_name ? ` — ${l.business_name}` : ''} (Lead)`,
      }));
    }
    if (showCustomers) {
      eligibleCustomers.forEach(c => opts.push({
        value: `customer:${c.id}`,
        label: `${c.full_name}${c.store_name ? ` — ${c.store_name}` : ''} (Customer)`,
      }));
    }
    return opts;
  }, [showLeads, showCustomers, eligibleLeads, eligibleCustomers]);

  // If the checkbox state changes and the currently-selected option is no
  // longer in the list, clear the selection rather than silently keeping
  // an invalid value.
  useEffect(() => {
    if (newSub.source_id && !dropdownOptions.find(o => o.value === newSub.source_id)) {
      setNewSub(prev => ({ ...prev, source_id: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOptions]);

  const handleAddSubscription = async (e) => {
    e.preventDefault();
    if (!newSub.source_id) return;

    const [sourceType, sourceId] = newSub.source_id.split(':');
    const payload = {
      plan_name: newSub.plan_name,
      monthly_amount: newSub.monthly_amount,
      payment_link: newSub.payment_link,
      lead_id: sourceType === 'lead' ? sourceId : null,
      store_request_id: sourceType === 'customer' ? sourceId : null,
    };

    setSubmitting(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/create-subscription.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setShowAddModal(false);
        setNewSub(EMPTY_NEW_SUB);
        setShowLeads(false);
        setShowCustomers(false);
        fetchPayments();
      } else {
        alert(data.message || 'Failed to create subscription');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setNewSub(EMPTY_NEW_SUB);
    setShowLeads(false);
    setShowCustomers(false);
  };

  // --- Edit drawer handlers ---
  const openEditDrawer = (subscription) => {
    setEditDrawer(subscription);
    setEditForm({
      plan_name: subscription.plan_name || '',
      monthly_amount: subscription.monthly_amount || '',
      payment_link: subscription.payment_link || '',
      payment_status: subscription.payment_status,
      subscription_status: subscription.subscription_status,
      next_billing_date: subscription.next_billing_date || '',
      grace_ends_date: subscription.grace_ends_date || '',
    });
  };

  const closeEditDrawer = () => {
    setEditDrawer(null);
    setEditForm({});
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/edit-subscription.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editDrawer.id, ...editForm })
      });
      const data = await res.json();
      if (data.success) {
        closeEditDrawer();
        fetchPayments();
      } else {
        alert(data.message || 'Failed to save changes');
      }
    } catch (err) {
      alert('Connection error');
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—';
  const formatMoney = (n) => `PKR ${Number(n).toLocaleString()}`;

  return (
    <div className="payments-page">
      <div className="payments-header">
        <div>
          <h1>Payments &amp; Subscriptions</h1>
          <p>Payment links, monthly subscriptions, grace periods, and disabled stores.</p>
        </div>
        <button className="btn-dark" onClick={() => setShowAddModal(true)}>+ Send Payment Link</button>
      </div>

      <div className="payments-stats-grid">
        <div className="pay-stat-card">
          <p className="pay-stat-title">Links Sent</p>
          <h3 className="pay-stat-value" style={{ color: '#1a1a1a' }}>{loading ? '—' : stats.links_sent}</h3>
        </div>
        <div className="pay-stat-card">
          <p className="pay-stat-title">Payments Paid</p>
          <h3 className="pay-stat-value" style={{ color: '#1e8a4c' }}>{loading ? '—' : stats.paid}</h3>
        </div>
        <div className="pay-stat-card">
          <p className="pay-stat-title">Failed / Grace</p>
          <h3 className="pay-stat-value" style={{ color: '#c2560a' }}>{loading ? '—' : stats.failed_or_grace}</h3>
        </div>
        <div className="pay-stat-card">
          <p className="pay-stat-title">Disabled Stores</p>
          <h3 className="pay-stat-value" style={{ color: '#c8342a' }}>{loading ? '—' : stats.disabled_stores}</h3>
        </div>
        <div className="pay-stat-card">
          <p className="pay-stat-title">Monthly Revenue</p>
          <h3 className="pay-stat-value" style={{ color: '#1e8a4c' }}>{loading ? '—' : formatMoney(stats.monthly_revenue)}</h3>
        </div>
      </div>

      {loading ? (
        <div className="payments-loading">Loading...</div>
      ) : subscriptions.length === 0 ? (
        <div className="payments-empty">No subscriptions yet — click "Send Payment Link" to add one.</div>
      ) : (
        <div className="payments-table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Store</th>
                <th>Plan</th>
                <th>Monthly</th>
                <th>Payment</th>
                <th>Subscription</th>
                <th>Next Billing</th>
                <th>Grace Ends</th>
                <th colSpan="5"></th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(s => {
                const isBusy = actionLoadingId === s.id;
                // Explicit numeric coercion — PHP/mysqli sends tinyint
                // columns as strings ("0"/"1"), and "0" is truthy in JS,
                // which silently broke every is_disabled check before.
                const isDisabled = Number(s.is_disabled) === 1;

                return (
                  <tr key={s.id}>
                    <td className="pay-client-name">{s.client_name}</td>
                    <td>{s.store_name || '—'}</td>
                    <td>{s.plan_name}</td>
                    <td>{formatMoney(s.monthly_amount)}</td>
                    <td><span className={`pay-pill pay-payment-${s.payment_status}`}>{PAYMENT_LABELS[s.payment_status]}</span></td>
                    <td><span className={`pay-pill pay-sub-${s.subscription_status}`}>{SUB_LABELS[s.subscription_status]}</span></td>
                    <td className="pay-muted">{formatDate(s.next_billing_date)}</td>
                    <td className="pay-muted">{formatDate(s.grace_ends_date)}</td>
                    <td><button className="pay-link-btn" onClick={() => handleCopy(s.payment_link)}>Copy</button></td>
                    <td><button className="pay-outline-btn" onClick={handleRemind}>Remind</button></td>
                    <td>
                      {s.payment_status !== 'failed' && s.subscription_status !== 'grace_period' && s.subscription_status === 'active' ? (
                        <button className="pay-dark-btn" disabled={isBusy} onClick={() => handleAction(s.id, 'mark_failed')}>Mark Failed</button>
                      ) : (
                        <button className="pay-dark-btn" disabled={isBusy} onClick={() => handleAction(s.id, 'mark_paid')}>Mark Paid</button>
                      )}
                    </td>
                    <td>
                      {!isDisabled && (s.payment_status === 'failed' || s.subscription_status === 'grace_period') ? (
                        <button className="pay-danger-btn" disabled={isBusy} onClick={() => { if (window.confirm(`Disable ${s.store_name || s.client_name}?`)) handleAction(s.id, 'disable'); }}>Disable</button>
                      ) : isDisabled ? (
                        <button className="pay-outline-btn" disabled={isBusy} onClick={() => handleAction(s.id, 'enable')}>Enable</button>
                      ) : (
                        <span className="pay-no-action">—</span>
                      )}
                    </td>
                    <td><button className="pay-outline-btn" onClick={() => openEditDrawer(s)}>Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="pay-modal-overlay" onClick={closeModal}>
          <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Send Payment Link</h2>
            <form onSubmit={handleAddSubscription}>
              <div className="form-group">
                <label>Show</label>
                <div className="pay-source-checkboxes">
                  <label className="pay-checkbox-label">
                    <input
                      type="checkbox"
                      checked={showLeads}
                      onChange={(e) => {
                        setShowLeads(e.target.checked);
                        if (e.target.checked) setShowCustomers(false);
                      }}
                    />
                    Leads
                  </label>
                  <label className="pay-checkbox-label">
                    <input
                      type="checkbox"
                      checked={showCustomers}
                      onChange={(e) => {
                        setShowCustomers(e.target.checked);
                        if (e.target.checked) setShowLeads(false);
                      }}
                    />
                    Customers
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Client *</label>
                <select
                  value={newSub.source_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    let planName = newSub.plan_name;

                    if (value) {
                      const [sourceType, sourceId] = value.split(':');
                      if (sourceType === 'lead') {
                        const lead = eligibleLeads.find(l => String(l.id) === sourceId);
                        planName = lead?.business_name?.trim() || 'Starter Store';
                      } else if (sourceType === 'customer') {
                        const customer = eligibleCustomers.find(c => String(c.id) === sourceId);
                        planName = customer?.store_name?.trim() || 'Starter Store';
                      }
                    }

                    setNewSub({ ...newSub, source_id: value, plan_name: planName });
                  }}
                  required
                  disabled={dropdownOptions.length === 0}
                >
                  <option value="">
                    {dropdownOptions.length === 0 ? 'Check Leads and/or Customers above' : 'Select a client'}
                  </option>
                  {dropdownOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Plan Name</label>
                <input type="text" value={newSub.plan_name} onChange={(e) => setNewSub({ ...newSub, plan_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Monthly Amount (PKR)</label>
                <input type="number" value={newSub.monthly_amount} onChange={(e) => setNewSub({ ...newSub, monthly_amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Payment Link</label>
                <input type="text" placeholder="https://..." value={newSub.payment_link} onChange={(e) => setNewSub({ ...newSub, payment_link: e.target.value })} />
              </div>
              <div className="pay-modal-actions">
                <button type="button" className="pay-outline-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-dark" disabled={submitting || !newSub.source_id}>{submitting ? 'Sending...' : 'Send Link'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editDrawer && (
        <div className="pay-drawer-overlay">
          <div className="pay-drawer-backdrop" onClick={closeEditDrawer}></div>
          <div className="pay-drawer">
            <div className="pay-drawer-header">
              <div>
                <h2>Edit Subscription</h2>
                <p className="pay-drawer-sub">{editDrawer.client_name}{editDrawer.store_name ? ` — ${editDrawer.store_name}` : ''}</p>
              </div>
              <button className="pay-drawer-close" onClick={closeEditDrawer}>×</button>
            </div>

            <form onSubmit={handleEditSave} className="pay-drawer-body">
              <div className="form-group">
                <label>Plan Name</label>
                <input type="text" value={editForm.plan_name} onChange={(e) => setEditForm({ ...editForm, plan_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Monthly Amount (PKR)</label>
                <input type="number" value={editForm.monthly_amount} onChange={(e) => setEditForm({ ...editForm, monthly_amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Payment Link</label>
                <input type="text" value={editForm.payment_link} onChange={(e) => setEditForm({ ...editForm, payment_link: e.target.value })} />
              </div>
              <div className="pay-drawer-row">
                <div className="form-group">
                  <label>Payment Status</label>
                  <select value={editForm.payment_status} onChange={(e) => setEditForm({ ...editForm, payment_status: e.target.value })}>
                    <option value="link_sent">Link Sent</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Subscription Status</label>
                  <select value={editForm.subscription_status} onChange={(e) => setEditForm({ ...editForm, subscription_status: e.target.value })}>
                    <option value="payment_due">Payment Due</option>
                    <option value="active">Active</option>
                    <option value="grace_period">Grace Period</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="pay-drawer-row">
                <div className="form-group">
                  <label>Next Billing Date</label>
                  <input type="date" value={editForm.next_billing_date} onChange={(e) => setEditForm({ ...editForm, next_billing_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Grace Ends Date</label>
                  <input type="date" value={editForm.grace_ends_date} onChange={(e) => setEditForm({ ...editForm, grace_ends_date: e.target.value })} />
                </div>
              </div>

              <div className="pay-drawer-actions">
                <button type="button" className="pay-outline-btn" onClick={closeEditDrawer}>Cancel</button>
                <button type="submit" className="btn-dark" disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;